import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { centralApi } from '../AxiosInstance/CreateInstance';
import {
	CandidatesResponse,
	InterviewAssignment,
	JobReq,
	MetaData,
	Recruiter,
	RequirementData,
	RoundTypeData,
} from '../Role_Layout/roleTypes';
import { toast } from 'react-toastify';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import Card, {
	CardActions,
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../components/bootstrap/Card';
import { CalendarDays, Clock, ListTodo, MapPin, UserSearch, Video } from 'lucide-react';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import Button, { ButtonGroup } from '../../components/bootstrap/Button';
import Icon from '../../components/icon/Icon';
import Spinner from '../../components/bootstrap/Spinner';
import useDarkMode from '../../hooks/useDarkMode';
import { SpinnerCircular } from 'spinners-react';
import Label from '../../components/bootstrap/forms/Label';
import ReactQuill from 'react-quill';
import NoDataImg from '../../assets/No-data-found.png';
import dayjs from 'dayjs';

interface IInterviewPropView {
	isFluid?: boolean;
}

const TABS: { [key: string]: { label: string; apiValue: string } } = {
	RequirenmentInfo: { label: 'Requirement Info', apiValue: 'requirement-info' },
	JobDesc: { label: 'Job Description', apiValue: 'jobdesc' },
	InterviewRound: { label: 'Interview Rounds', apiValue: 'interview-round' },
};
interface Interview {
	_id: string;
	jobRequisitionId: string;
	roundType: string;
	mode: 'online' | 'offline';
	interviewLength: string;
	evaluationFormTemplateId: string;
	interviewers: [
		{
			id: string;
			name: string;
			role: string;
			department: string;
		},
	];
	assignedRecruiter: string;
	scheduledTime: string;
	created_by: string;
	updated_by: string | null;
	assignedAt: string;
	createdAt: string;
}

const InterviewView: React.FC<IInterviewPropView> = ({ isFluid }) => {
	const { id } = useParams();
	const [requirement, setRequirement] = useState<RequirementData>();
	const [meta, setMeta] = useState<MetaData>();
	const [recruiter, setRecruiter] = useState<Recruiter>();
	const [interviews, setInterviews] = useState<Interview[]>([]);
	const [jobreq, setJobReq] = useState<JobReq>();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { themeStatus, darkModeStatus } = useDarkMode();
	const [activeTab, setActiveTab] = useState<keyof typeof TABS | string>('RequirenmentInfo');
	const customOrder = ['L1', 'L2', 'HR', 'offer letter'];

	const fetchJobReq = useCallback(async () => {
		try {
			setLoading(true);
			let allData: JobReq[] = [];
			const limit = 50;

			const firstRes = await centralApi.get(
				`jobService/jobRequest/list?pageNo=1&limit=${limit}`,
			);
			const firstPageData: JobReq[] = firstRes.data.data;
			const total = firstRes.data.total || firstPageData.length;
			allData = [...allData, ...firstPageData];

			const totalPages = Math.ceil(total / limit);

			const promises = [];
			for (let i = 2; i <= totalPages; i += 1) {
				promises.push(
					centralApi.get(`jobService/jobRequest/list?pageNo=${i}&limit=${limit}`),
				);
			}

			const results = await Promise.all(promises);
			results.forEach((res) => {
				allData = [...allData, ...res.data.data];
			});

			const filterList = allData.find((item: JobReq) => item._id === id);
			setJobReq(filterList);
		} catch (error) {
			// toast.error('Error while fetching Job Requisition', { closeOnClick: true });
		} finally {
			setLoading(false);
		}
	}, [id]);
	useEffect(() => {
		const fetchRequirenments = async () => {
			try {
				await centralApi
					.post('jobService/jobRequest/requirement', {
						jobId: id,
					})
					.then((res) => {
						setRequirement(res.data.data);
					});
			} catch (error) {
				// toast.error('Error while fetching requirement', { closeOnClick: true });
			}
		};

		const fetchMetaData = async () => {
			try {
				await centralApi
					.post('jobService/jobRequest/meta', {
						jobId: id,
					})
					.then((res) => {
						setMeta(res.data.data);
					});
			} catch (error) {
				// toast.error('Error while fetching metadata', { closeOnClick: true });
			}
		};

		const fetchRecruiter = async () => {
			try {
				await centralApi
					.post('jobService/jobRequest/recruiter', {
						jobId: id,
					})
					.then((res) => {
						setRecruiter(res.data.data[0]);
					});
			} catch (error) {
				// toast.error('Error while fetching recruiter', { closeOnClick: true });
			}
		};

		fetchRequirenments();
		fetchMetaData();
		fetchRecruiter();
	}, [id]);

	const fetchInterview = useCallback(async () => {
		setLoading(true);
		try {
			const allInterviews: Interview[] = [];
			const pageNo = 1;
			const limit = 50;

			const firstRes = await centralApi.get(
				`jobService/interviewPanel/list?limit=${limit}&pageNo=${pageNo}`,
			);
			const { data: firstData, total } = firstRes.data;
			if (Array.isArray(firstData)) {
				allInterviews.push(...firstData);
			}

			const totalPages = Math.ceil(total / limit);

			const promises = [];
			for (let i = 2; i <= totalPages; i += 1) {
				promises.push(
					centralApi.get(`jobService/interviewPanel/list?limit=${limit}&pageNo=${i}`),
				);
			}

			const results = await Promise.all(promises);
			results.forEach((res) => {
				if (Array.isArray(res.data.data)) {
					allInterviews.push(...res.data.data);
				}
			});

			const filterList = allInterviews.filter(
				(item: Interview) => item.jobRequisitionId === id,
			);

			setInterviews(filterList);
		} catch (error) {
			// toast.error('Error while fetching interviews', { closeOnClick: true });
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		fetchInterview();
		fetchJobReq();
	}, [fetchJobReq, fetchInterview]);

	function capitalizeWords(str: string | undefined): string {
		if (!str) return '';
		return str
			.toLowerCase()
			.split(' ')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	return (
		<Page container='fluid'>
			<Card>
				<CardHeader borderSize={1}>
					<CardLabel iconColor='info' className='ms-2'>
						<CardTitle>
							<UserSearch className='me-2 text-info' />
							{capitalizeWords(jobreq?.designation)}
						</CardTitle>
					</CardLabel>
					<CardActions>
						<Button
							size='lg'
							className='ps-2 pe-2 py-1 d-inline-block'
							style={{
								backgroundColor:
									`${requirement?.urgency === 'high' ? '#F8D7DA' : ''}` ||
									`${requirement?.urgency === 'low' ? '#D4EDDA' : ''}` ||
									`${requirement?.urgency === 'medium' ? '#FFF3CD' : ''}`,
								color:
									`${requirement?.urgency === 'high' ? '#721C24' : ''}` ||
									`${requirement?.urgency === 'low' ? '#155724' : ''}` ||
									`${requirement?.urgency === 'medium' ? '#856404' : ''}`,
								borderRadius: '0.5rem',
								fontWeight: 500,
								fontSize: '0.8rem',
								minWidth: '80px',
								textAlign: 'center',
							}}>
							{`${capitalizeWords(requirement?.urgency)} Priority` || 'N/A'}
						</Button>
					</CardActions>
				</CardHeader>
				<SubHeaderLeft className="ps-4 mt-2">
					<Button
						icon='ArrowBack'
						onClick={() => navigate('/jobRequisition')}
						className='hrms-gradient-btn'>
						Back to List
					</Button>
				</SubHeaderLeft>
				<CardBody>
					<Card className='p-2'>
						<div className='row'>
							<div className='col'>
								<ButtonGroup className='d-flex gap-2'>
									{Object.keys(TABS).map((key) => {
										return (
											<Button
												key={key}
												style={{ borderRadius: '0.5rem' }}
												color='dark'
												className={
													activeTab === key ? 'hrms-gradient-btn' : themeStatus
												}
												onClick={() => setActiveTab(key)}>
												{TABS[key].label}
											</Button>
										);
									})}
								</ButtonGroup>
							</div>
						</div>
					</Card>
				</CardBody>
				{activeTab === 'RequirenmentInfo' && (
					<Card className='mx-2'>
						<CardHeader borderSize={1}>
							<CardLabel>
								<CardTitle>
									<ListTodo className='me-2 text-info' />
									Requirement Information
								</CardTitle>
							</CardLabel>
						</CardHeader>
						<CardBody>
							<div className='row g-3 mb-4'>
								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='person' size='lg' /> Role
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{requirement?.designation || 'N/A'}
										</div>
									)}
								</div>
								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='Apartment' size='lg' /> Department
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{meta?.departmentName || 'N/A'}
										</div>
									)}
								</div>
							</div>

							<div className='row g-3 mb-4'>
								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='Money' size='lg' /> Budget
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{capitalizeWords(requirement?.budget) || 'N/A'}
										</div>
									)}
								</div>

								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='AccessTime' size='lg' /> Urgency
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div>
											{(() => {
												let bgColor = '';
												let textColor = '';

												if (requirement?.urgency === 'high') {
													bgColor = '#F8D7DA';
													textColor = '#721C24';
												} else if (requirement?.urgency === 'low') {
													bgColor = '#D4EDDA';
													textColor = '#155724';
												} else if (requirement?.urgency === 'medium') {
													bgColor = '#FFF3CD';
													textColor = '#856404';
												}

												return (
													<span
														className='ps-2 pe-2 py-1 d-inline-block'
														style={{
															backgroundColor: bgColor,
															color: textColor,
															borderRadius: '999px',
															fontWeight: 500,
															fontSize: '0.8rem',
															minWidth: '80px',
															textAlign: 'center',
														}}>
														{capitalizeWords(
															requirement?.urgency,
														) || 'N/A'}
													</span>
												);
											})()}
										</div>
									)}
								</div>

								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='AccessibilityNew' size='lg' /> No. Resource
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											<span className='ms-2'>
												{requirement?.noOfResourses || 'N/A'}
											</span>
										</div>
									)}
								</div>
							</div>

							<div className='row g-3'>
								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='person' size='lg' /> Created By
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{capitalizeWords(meta?.createdBy?.name) || 'N/A'}
										</div>
									)}
								</div>

								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='PersonAdd' size='lg' /> Assign Recruiter
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{capitalizeWords(recruiter?.name) || 'N/A'}
										</div>
									)}
								</div>

								<div className='col-12 col-md-4'>
									<div className='text-muted'>
										<Icon icon='assignment' size='lg' /> JD Attach By
									</div>
									{loading ? (
										<>
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
											<Spinner color='success' isSmall inButton isGrow />
										</>
									) : (
										<div className='fw-semibold'>
											{capitalizeWords(meta?.jdAttachedBy?.name) || 'N/A'}
										</div>
									)}
								</div>
							</div>
						</CardBody>
					</Card>
				)}
				{activeTab === 'JobDesc' && (
					<Card>
						<CardHeader
							borderSize={1}
							className='d-flex justify-content-between align-items-center'>
							<CardLabel>
								<CardTitle className='d-flex align-items-center'>
									<ListTodo className='me-2 text-info' />
									Job Description
								</CardTitle>
							</CardLabel>
						</CardHeader>

						<CardBody className='d-flex flex-column gap-3'>
							{(() => {
								if (loading) {
									return (
										<div
											className='d-flex justify-content-center align-items-center w-100'
											style={{ minHeight: '50vh' }}>
											<SpinnerCircular
												size={50}
												thickness={100}
												speed={100}
												color={darkModeStatus ? '#fff' : '#4ecdc4'}
												secondaryColor={
													darkModeStatus ? '#4ecdc4' : '#f1f1f1'
												}
											/>
										</div>
									);
								}

								if (jobreq?.jdId === null) {
									return (
										<div
											className='d-flex flex-column w-100 justify-content-center align-items-center'
											style={{ minHeight: '100%', height: '100%' }}>
											<img
												src={NoDataImg}
												alt='No Data'
												width='200px'
												height='200px'
											/>
											<p className='text-muted fs-5 mt-3'>
												No Template found
											</p>
										</div>
									);
								}

								if (jobreq?.jdId !== null) {
									return (
										<>
											<div className='d-flex justify-content-between align-items-start flex-wrap mb-3'>
												<div>
													<div className='text-muted'>
														<span className='mx-1'>JD Status</span>
													</div>
													<div>
														<span className='badge bg-success-subtle text-success px-3 py-2'>
															{capitalizeWords(
																jobreq?.status || 'Approved',
															)}
														</span>
													</div>
												</div>

												<div className='text-end'>
													{jobreq?.approved_by ? (
														<>
															<div className='text-muted'>
																Approved By:{' '}
																{capitalizeWords(jobreq?.approved_by?.name || 'N/A')}
															</div>
															<div className='fw-medium'>
																{jobreq?.approved_at
																	? dayjs(jobreq.approved_at).format('M/D/YYYY, h:mm A')
																	: 'N/A'}
															</div>
														</>
													) : (
														<>
															<div className='text-muted'>
																Attached By:{' '}
																{capitalizeWords(meta?.jdAttachedBy?.name || 'N/A')}
															</div>
															<div className='fw-medium'>
																{meta?.jd_attached_at
																	? dayjs(meta.jd_attached_at).format('M/D/YYYY, h:mm A')
																	: 'N/A'}
															</div>
														</>
													)}
												</div>
											</div>

											<Label
												htmlFor='text-editor'
												className='fw-semibold mb-2'>
												JD Content Preview
											</Label>

											<div
												className='border rounded bg-dark p-3 mb-3'
												style={{
													maxHeight: '400px',
													overflowY: 'auto',
													overflowX: 'hidden',
													width: '100%',
												}}>
												<ReactQuill
													readOnly
													className='text-light'
													theme='snow'
													modules={{ toolbar: false }}
													value={requirement?.jdContent}
													style={{
														minHeight: '100px',
														fontSize: '1rem',
														color: '#343a40',
													}}
												/>
											</div>
										</>
									);
								}

								return (
									<div className='text-center text-muted'>
										No Template Found
									</div>
								);
							})()}
						</CardBody>
					</Card>
				)}
				{activeTab === 'InterviewRound' && (
					<Card>
						<CardHeader
							borderSize={1}
							className='d-flex justify-content-between align-items-center'>
							<CardLabel>
								<CardTitle className='d-flex align-items-center'>
									<Clock className='me-2 text-info' />
									Interview Rounds
								</CardTitle>
							</CardLabel>
						</CardHeader>

						<CardBody className='d-flex flex-column gap-4'>
							{(() => {
								if (loading) {
									return (
										<div
											className='d-flex justify-content-center align-items-center'
											style={{ minHeight: '50vh' }}>
											<SpinnerCircular
												size={50}
												thickness={100}
												speed={100}
												color={darkModeStatus ? '#fff' : '#4ecdc4'}
												secondaryColor={
													darkModeStatus ? '#4ecdc4' : '#f1f1f1'
												}
											/>
										</div>
									);
								}

								if (interviews.length === 0) {
									return (
										<div
											className='d-flex flex-column w-100 justify-content-center align-items-center'
											style={{ minHeight: '100%', height: '100%' }}>
											<img
												src={NoDataImg}
												alt='No Data'
												width='200px'
												height='200px'
											/>
											<p className='text-muted fs-5 mt-3'>
												No interviews found
											</p>
										</div>
									);
								}

								if (interviews.length > 0) {
									const sortedInterviews = [...interviews].sort((a, b) => {
										const aIdx = customOrder.indexOf(a.roundType);
										const bIdx = customOrder.indexOf(b.roundType);
										return (aIdx === -1 ? Infinity : aIdx) - (bIdx === -1 ? Infinity : bIdx);
									})
									return sortedInterviews.map((item, idx) => {
										const roundLabel = `Round ${idx + 1} – ${item.roundType}`;
										const initials = item.roundType
											.slice(0, 2)
											.toUpperCase();
										const modeIcon =
											item.mode === 'online' ? (
												<Video
													color='green'
													size={16}
													className='me-1'
												/>
											) : (
												<MapPin
													color='blue'
													size={16}
													className='me-1'
												/>
											);
										const status = item.scheduledTime
											? 'Scheduled'
											: 'Pending';

										return (
											<div
												key={item._id}
												className='d-flex justify-content-between align-items-start rounded p-3 shadow-sm'
												style={{
													border: '1px solid #e0e0e0',
													padding: '1.25rem',
													borderRadius: '12px',
												}}>
												<div className='d-flex'>
													<div
														className='rounded-circle bg-info text-light fw-bold d-flex align-items-center justify-content-center me-3'
														style={{
															width: 45,
															height: 45,
															fontSize: '0.9rem',
														}}>
														{initials}
													</div>

													<div>
														<div className='fw-semibold fs-6 mb-1'>
															{roundLabel}
														</div>
														<div className='text-muted small mb-2'>
															Interviewers:{' '}
															{item.interviewers
																.map((i) => i.name)
																.join(', ') || '-'}
														</div>

														<div className='d-flex flex-wrap gap-4 text-muted small'>
															<div className='d-flex align-items-center'>
																{modeIcon}
																<span className='fw-semibold text-light ms-1'>
																	Mode:{' '}
																	{capitalizeWords(item.mode)}
																</span>
															</div>
															{item.scheduledTime && (
																<div className='d-flex align-items-center'>
																	<CalendarDays
																		color='purple'
																		size={16}
																		className='me-1'
																	/>
																	<span className='fw-semibold text-light'>
																		Scheduled:{' '}
																		{dayjs(
																			item.scheduledTime,
																			'HH:mm',
																		).format('hh:mm A')}
																	</span>
																</div>
															)}
														</div>
													</div>
												</div>

												<div className='d-flex flex-column align-items-end justify-content-between ms-3'>
													<span
														className={`badge px-3 py-2 rounded-pill mb-3 ${status === 'Scheduled'
															? 'bg-success-subtle text-success'
															: 'bg-warning-subtle text-warning'
															}`}>
														{status}
													</span>

													<Button
														className='btn btn-outline-primary fw-semibold'
														style={{
															borderRadius: '8px',
															boxShadow:
																'0 2px 6px rgba(0,0,0,0.05)',
															fontSize: '0.9rem',
														}}
														onClick={() =>
															navigate(
																`/jobRequisition/feedback-form/${item.roundType}/${item.evaluationFormTemplateId}`,
															)
														}>
														Feedback
													</Button>
												</div>
											</div>
										);
									});
								}

								return (
									<div className='text-center text-muted'>
										No Interview Rounds Found
									</div>
								);
							})()}
						</CardBody>
					</Card>
				)}
			</Card>
		</Page>
	);
};

InterviewView.defaultProps = {
	isFluid: false,
};

export default InterviewView;
